import {
  Modal,
  ModalBody,
  ModalFooter,
  Button,
  Card,
  CardBody,
} from '@kollektive/design-system';
import { AlertTriangle } from 'lucide-react';
import type { TeamMember } from '@/hooks/useTeam';
import styles from './RemoveMemberDialog.module.css';

interface RemoveMemberDialogProps {
  member: TeamMember;
  memberCount?: number;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  isLoading: boolean;
}

export function RemoveMemberDialog({
  member,
  memberCount = 1,
  isOpen,
  onClose,
  onConfirm,
  isLoading,
}: RemoveMemberDialogProps) {
  const isBulk = memberCount > 1;

  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  return (
    <Modal open={isOpen} onClose={handleClose} size="sm">
      <ModalBody>
        <div className={styles.content}>
          {/* Warning icon */}
          <div className={styles.iconWrapper}>
            <AlertTriangle size={24} className={styles.errorIcon} />
          </div>

          <h2 className={styles.title}>
            {isBulk ? `Remove ${memberCount} Members` : 'Remove Team Member'}
          </h2>

          <p className={styles.description}>
            {isBulk ? (
              <>
                Are you sure you want to remove <strong>{memberCount} members</strong> from your workspace?
                They will lose access immediately but can be re-invited later.
              </>
            ) : (
              <>
                Are you sure you want to remove <strong>{member.name}</strong> from your workspace?
                They will lose access immediately but can be re-invited later.
              </>
            )}
          </p>

          {/* Member info - only show for single member */}
          {!isBulk && (
            <Card>
              <CardBody>
                <div className={styles.memberInfo}>
                  <div className={styles.infoRow}>
                    <span className={styles.infoLabel}>Name</span>
                    <span className={styles.infoValue}>{member.name}</span>
                  </div>
                  <div className={styles.infoRow}>
                    <span className={styles.infoLabel}>Email</span>
                    <span className={styles.infoValue}>{member.email}</span>
                  </div>
                  <div className={styles.infoRow}>
                    <span className={styles.infoLabel}>Role</span>
                    <span className={styles.infoValue}>{member.role}</span>
                  </div>
                </div>
              </CardBody>
            </Card>
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button
          variant="secondary"
          onClick={handleClose}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          variant="danger"
          onClick={onConfirm}
          disabled={isLoading}
        >
          {isLoading
            ? 'Removing...'
            : isBulk
              ? `Remove ${memberCount} Members`
              : 'Remove Member'}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
